import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";

const MENU_WIDTH = 220;
const VP_PAD = 8;
const MIN_HEIGHT = 80;
const PREFER_BELOW_THRESHOLD = 150; // px — open below if at least this much space

/** top XOR bottom will be set depending on flip direction */
type MenuPos = { top?: number; bottom?: number; left: number; maxHeight: number };

function calcPosition(trigger: HTMLElement): MenuPos {
    const r = trigger.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Horizontal: prefer left-aligned, flip to right-align if it would clip
    let left = r.left;
    if (left + MENU_WIDTH > vw - VP_PAD) left = r.right - MENU_WIDTH;
    left = Math.max(VP_PAD, Math.min(left, vw - MENU_WIDTH - VP_PAD));

    const spaceBelow = vh - r.bottom - VP_PAD;
    const spaceAbove = r.top - VP_PAD;

    if (spaceBelow >= PREFER_BELOW_THRESHOLD || spaceBelow >= spaceAbove) {
        // Open downward
        const maxHeight = Math.min(320, Math.max(MIN_HEIGHT, spaceBelow));
        return { top: r.bottom + 4, left, maxHeight };
    } else {
        // Flip upward — anchor the menu's bottom edge just above the trigger
        const maxHeight = Math.min(320, Math.max(MIN_HEIGHT, spaceAbove));
        return { bottom: vh - r.top + 4, left, maxHeight };
    }
}

type InlineFilterDropdownProps = Readonly<{
    label: string;
    options: string[];
    selected: string[];
    onChange: (selected: string[]) => void;
    formatOption?: (value: string) => string;
    searchable?: boolean;
}>;

/**
 * Inline dropdown filter for table column headers.
 * Selection-based: default all unchecked = show all, check items to filter.
 * Selections are buffered locally and only applied when the user clicks Save.
 */
export function InlineFilterDropdown({
    label,
    options,
    selected,
    onChange,
    formatOption,
    searchable = false,
}: InlineFilterDropdownProps) {
    const [open, setOpen] = useState(false);
    const [localSelected, setLocalSelected] = useState<string[]>(selected);
    const [search, setSearch] = useState("");
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLFieldSetElement>(null);
    const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

    // Sync local state when parent selected changes (e.g. after API response)
    useEffect(() => {
        setLocalSelected(selected);
    }, [selected]);

    // Reset local state when opening
    useEffect(() => {
        if (open) {
            setLocalSelected(selected);
            setSearch("");
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        function handleClick(e: MouseEvent) {
            const target = e.target as Node;
            const clickedTrigger = !!triggerRef.current?.contains(target);
            const clickedMenu = !!menuRef.current?.contains(target);
            if (!clickedTrigger && !clickedMenu) {
                setOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClick);
        return () => document.removeEventListener("mousedown", handleClick);
    }, [open]);

    const reposition = useCallback(() => {
        if (triggerRef.current) setMenuPos(calcPosition(triggerRef.current));
    }, []);

    useEffect(() => {
        if (!open) { setMenuPos(null); return; }
        reposition();
        window.addEventListener("resize", reposition);

        // Close if something outside the menu scrolls (e.g. horizontal table scroll)
        function handleScroll(e: Event) {
            if (menuRef.current?.contains(e.target as Node)) return;
            setOpen(false);
        }
        window.addEventListener("scroll", handleScroll, true);

        // Block wheel / touch-scroll on the whole page while the dropdown is open.
        // Events originating inside the option list are allowed through so the
        // user can still scroll a long list of options.
        function blockScroll(e: WheelEvent | TouchEvent) {
            if (menuRef.current?.contains(e.target as Node)) return;
            e.preventDefault();
        }
        document.addEventListener("wheel", blockScroll, { passive: false });
        document.addEventListener("touchmove", blockScroll, { passive: false });

        return () => {
            window.removeEventListener("resize", reposition);
            window.removeEventListener("scroll", handleScroll, true);
            document.removeEventListener("wheel", blockScroll);
            document.removeEventListener("touchmove", blockScroll);
        };
    }, [open, reposition]);

    const hasFilter = selected.length > 0;
    const displayLabel = hasFilter ? `${label} (${selected.length})` : label;

    function handleToggle(option: string) {
        setLocalSelected((prev) => {
            const isSelected = prev.includes(option);
            return isSelected ? prev.filter((v) => v !== option) : [...prev, option];
        });
    }

    function handleClear() {
        onChange([]);
        setOpen(false);
    }

    function handleSave() {
        onChange(localSelected);
        setOpen(false);
    }

    const localHasChanges =
        localSelected.length !== selected.length ||
        localSelected.some((v) => !selected.includes(v));

    const visibleOptions = useMemo(() => {
        if (!searchable || !search.trim()) return options;
        const q = search.trim().toLowerCase();
        return options.filter((o) => {
            const display = formatOption ? formatOption(o) : o;
            return display.toLowerCase().includes(q);
        });
    }, [options, search, searchable, formatOption]);

    if (options.length === 0) {
        return <span style={{ fontWeight: 700 }}>{label}</span>;
    }

    return (
        <div style={{ position: "relative", display: "inline-block" }}>
            <button
                ref={triggerRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-haspopup="dialog"
                aria-expanded={open}
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    font: "inherit",
                    color: hasFilter ? "var(--primary-blue)" : "inherit",
                    fontWeight: 700,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                }}
            >
                {displayLabel}
                <span style={{ fontSize: 10, lineHeight: 1 }}>{open ? "▴" : "▾"}</span>
            </button>
            {open && menuPos && createPortal(
                <fieldset
                    ref={menuRef}
                    style={{
                        position: "fixed",
                        ...(menuPos.bottom !== undefined
                            ? { bottom: menuPos.bottom }
                            : { top: menuPos.top }),
                        left: menuPos.left,
                        minWidth: 200,
                        maxHeight: menuPos.maxHeight,
                        background: "var(--white)",
                        border: "1px solid var(--border)",
                        borderRadius: 8,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.14), 0 2px 6px rgba(0,0,0,0.06)",
                        zIndex: 9900,
                        padding: "6px 0",
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        margin: 0,
                    }}
                >
                    <legend style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
                        {label} filter options
                    </legend>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "6px 14px", borderBottom: "1px solid var(--border)", marginBottom: 2 }}>
                        {hasFilter && (
                            <button
                                type="button"
                                onClick={handleClear}
                                style={{
                                    padding: "4px 12px",
                                    border: "none",
                                    background: "transparent",
                                    color: "var(--error-red)",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Clear
                            </button>
                        )}
                        {localHasChanges && (
                            <button
                                type="button"
                                onClick={handleSave}
                                className="primary"
                                style={{
                                    padding: "4px 12px",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    borderRadius: 6,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Save
                            </button>
                        )}
                    </div>
                    {searchable && (
                        <div style={{ padding: "4px 10px 6px" }}>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search…"
                                autoFocus
                                style={{
                                    width: "100%",
                                    padding: "5px 8px",
                                    fontSize: 12,
                                    border: "1px solid var(--border)",
                                    borderRadius: 5,
                                    outline: "none",
                                    boxSizing: "border-box",
                                }}
                            />
                        </div>
                    )}
                    <div style={{ flex: 1, overflowY: "auto", overscrollBehavior: "contain" }}>
                        {visibleOptions.map((option) => {
                            const isChecked = localSelected.includes(option);
                            return (
                                <label
                                    key={option}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "6px 14px",
                                        cursor: "pointer",
                                        fontSize: 13,
                                        color: "var(--text-dark)",
                                        background: isChecked ? "var(--light-blue)" : "transparent",
                                    }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => handleToggle(option)}
                                        style={{ margin: 0 }}
                                    />
                                    <span>{formatOption ? formatOption(option) : option}</span>
                                </label>
                            );
                        })}
                    </div>
                </fieldset>
            , document.body)}
        </div>
    );
}
